import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import {
  AutoCompleteExtraProps,
  CurrencyFieldExtraProps,
  FormHandles,
  GenericFormSection,
  NumberFormatFieldExtraProps,
  TableListFieldProps,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { CommonConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { ReceiptFormTypes } from "models/formTypes";
import { ReceiptDetailsFormProps } from "models/propTypes/receiptPropTypes";
import { User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region  components
import { GenericForm } from "components/common";
import ReceiptPersonFormContainer from "./receiptPersonFormContainer";
import { CostForwardingDetailsSection } from "components/costForwardingMarking";
import { useSearchService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper, UtilsHelper } from "utils/helpers";

const ReceiptDetailsForm: ForwardRefRenderFunction<FormHandles, ReceiptDetailsFormProps> = (props, ref) => {
  const searchService = useSearchService();
  const {
    receipt,
    currency,
    dimensionAmount,
    amountsChangeHandler,
    currencyChangeHandler,
    receiptCategories,
    paymentMethods,
    paymentMethodSelectHandler,
    purchaseDateChangeHandler,
    receiptCategorySelectHandler,
    paymentAddActions,
    fileViewer,
    isDisabledForWintCardReceiptDraft,
    canPayEmployeeSelf,
    isRepresentation,
    isOtherPaymentMethod,
  } = props;

  const formFields = useMemo((): GenericFormSection<ReceiptFormTypes.Details>[] => {
    return [
      {
        label: TranslationKeys.Common.General,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WInfo,
        innerFields: {
          createdBy: {
            label: TranslationKeys.Common.SubmittedBy,
            type: FormEnums.FormFieldType.TEXT,
            href: UrlHelper.formatPath(`/Employee/${receipt.createdByPersonId}`),
            disabled: true,
          },
          supplierName: {
            label: TranslationKeys.Supplier.Supplier,
            type: FormEnums.FormFieldType.TEXT,
            descriptionText: TranslationKeys.Receipt.SupplierDescriptionText,
            disabled: receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard,
          },
          receiptCategory: {
            label: TranslationKeys.Common.RefersTo,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            descriptionText: TranslationKeys.Receipt.ReceiptRefersToHelptext,
            extraProps: {
              referenceFieldName: FormFieldConstants.Receipt.Details.ReceiptCategoryId,
              items: receiptCategories,
              onItemSelect: receiptCategorySelectHandler,
            } as AutoCompleteExtraProps<ReceiptFormTypes.Details>,
          },
          otherExplanation: {
            label: TranslationKeys.Receipt.ReceiptOtherExplanationHelptext,
            type: FormEnums.FormFieldType.TEXT,
            maxLength: 300,
            hidden: (values: ReceiptFormTypes.Details) =>
              values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.Other,
          },
          intent: {
            label: TranslationKeys.Common.Purpose,
            descriptionText: TranslationKeys.Receipt.ReceiptPurposeHelptext,
            type: FormEnums.FormFieldType.TEXTAREA,
            maxLength: 300,
            hidden: isRepresentation,
          },
          persons: {
            label: TranslationKeys.Common.Persons,
            type: FormEnums.FormFieldType.TABLELIST,
            descriptionText: TranslationKeys.Receipt.ReceiptPersonHelpText,
            hidden: isRepresentation,
            extraProps: {
              resultTableProperties: {
                name: {
                  formatter: (value: WintSuperkollAPIModel.ReceiptPerson) => new User(0, value.name),
                },
                company: {
                  formatter: (value: WintSuperkollAPIModel.ReceiptPerson) => value.company,
                },
              },
              addItemForm: (
                <ReceiptPersonFormContainer
                  handleSubmit={async (values: WintSuperkollAPIModel.ReceiptPerson) => {
                    return { ...values, id: UtilsHelper.getUniqueId() };
                  }}
                />
              ),
              icon: IconDefinitionConstants.WUser,
              entityText: TranslationKeys.Common.Persons,
              disableSort: true,
            } as TableListFieldProps<WintSuperkollAPIModel.ReceiptPerson>,
          },
          internalNote: {
            label: TranslationKeys.Receipt.InternalNote,
            descriptionText: TranslationKeys.Receipt.InternalNoteDescriptionText,
            type: FormEnums.FormFieldType.TEXTAREA,
          },
        },
      },
      {
        label: TranslationKeys.Common.Amount,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WCalculator,
        innerFields: {
          amount: {
            label: TranslationKeys.Common.AmountInclVAT,
            type: FormEnums.FormFieldType.CURRENCY,
            descriptionText: TranslationKeys.Receipt.ReceiptAmountHelptext,
            onChangeHandler: amountsChangeHandler,
            disabled: isDisabledForWintCardReceiptDraft,
            extraProps: {
              currencyReferenceFieldName: FormFieldConstants.Receipt.Details.Currency,
            } as CurrencyFieldExtraProps<ReceiptFormTypes.Details>,
          },
          currency: {
            label: TranslationKeys.Common.Currency,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            descriptionText: TranslationKeys.Receipt.ReceiptCurrencyHelptext,
            onChangeHandler: currencyChangeHandler,
            disabled: isDisabledForWintCardReceiptDraft,
            extraProps: {
              items: searchService.currency,
            } as AutoCompleteExtraProps<ReceiptFormTypes.Details>,
          },
        },
      },
      {
        label: TranslationKeys.Common.Dates,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WCalendar,
        innerFields: {
          purchaseDate: {
            label: TranslationKeys.Receipt.PurchaseDate,
            controlLabel: TranslationKeys.Receipt.PurchaseDate,
            type: FormEnums.FormFieldType.DATE,
            descriptionText: TranslationKeys.Receipt.ReceiptPurchaseDateHelptext,
            onChangeHandler: purchaseDateChangeHandler,
            disabled: isDisabledForWintCardReceiptDraft,
          },
        },
      },
      {
        label: TranslationKeys.Common.PaymentDetails,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WTransactions,
        innerFields: {
          paymentMethod: {
            label: TranslationKeys.Receipt.PaymentMethod,
            descriptionText: TranslationKeys.Receipt.ReceiptPaymentMethodHelptext,
            type: FormEnums.FormFieldType.AUTOCOMPLETE,
            extraProps: {
              referenceFieldName: FormFieldConstants.Receipt.Details.PaymentMethodId,
              items: paymentMethods,
              actions: paymentAddActions,
              onItemSelect: paymentMethodSelectHandler,
            } as AutoCompleteExtraProps<ReceiptFormTypes.Details>,
            disabled: (values: ReceiptFormTypes.Details) =>
              values.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard ||
              values.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.MissingDocument,
          },
          otherPaymentMethodExplanation: {
            label: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Receipt.ClassifiedReceiptExplanationHelptext
            ),
            type: FormEnums.FormFieldType.TEXT,
            maxLength: 300,
            hidden: (values: ReceiptFormTypes.Details) => !isOtherPaymentMethod(values),
          },
          paymentStatus: {
            label: TranslationKeys.Receipt.PaymentStatus,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
          },
          recipientAccount: {
            label: TranslationKeys.Receipt.RecipientAccount,
            type: FormEnums.FormFieldType.TEXT,
            disabled: true,
            hidden: (values: ReceiptFormTypes.Details) =>
              values.paymentMethodType !== WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense ||
              !values.canPayEmployee,
          },
          paymentDate: {
            label: TranslationKeys.Common.PaymentDate,
            controlLabel: TranslationKeys.Common.PaymentDate,
            type: FormEnums.FormFieldType.DATE,
            disabled: true,
            hidden: (values: ReceiptFormTypes.Details) => values.paymentDate === undefined,
          },
          isManualPayment: {
            label: TranslationKeys.Payments.ManualPayment,
            descriptionText: TranslationKeys.Receipt.ManualPaymentDescription,
            type: FormEnums.FormFieldType.TOGGLE,
            hidden: canPayEmployeeSelf,
          },
          personNumber: {
            label: TranslationKeys.Settings.PersonNumber,
            type: FormEnums.FormFieldType.NUMBER,
            extraProps: {
              allowLeadingZeros: true,
              format: CommonConstants.PersonNumberMask,
              formattedValueReferenceFieldName: FormFieldConstants.Settings.EmployeeSettings.PersonNumberFormatted,
            } as NumberFormatFieldExtraProps,
            hidden: canPayEmployeeSelf,
          },
          clearingNumber: {
            label: TranslationKeys.Settings.ClearingNumber,
            descriptionText: TranslationKeys.Settings.ClearingNumberDescription,
            type: FormEnums.FormFieldType.TEXT,
            hidden: canPayEmployeeSelf,
          },
          bankAccount: {
            label: TranslationKeys.Settings.AccountNumber,
            descriptionText: TranslationKeys.Settings.AccountNumberDescription,
            type: FormEnums.FormFieldType.TEXT,
            hidden: canPayEmployeeSelf,
          },
        },
      },
      {
        label: TranslationKeys.Common.Attach,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WDocument,
        hidden: props.isReadOnly,
        innerFields: {
          filesToUpload: {
            label: TranslationKeys.Common.Attachments,
            type: FormEnums.FormFieldType.FILEUPLOADER,
            extraProps: {
              fileUploaderType: "full",
            },
          },
        },
      },
      {
        label: TranslationKeys.Common.Tags,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: IconDefinitionConstants.WHashtag,
        hidden: {
          referenceFieldName: FormFieldConstants.Receipt.Details.ShowDimensions,
          hiddenWhenEqualTo: false,
        },
        innerFields: {
          dimensions: {
            label: "",
            type: FormEnums.FormFieldType.DIMENSIONSELECTOR,
            extraProps: {
              amount: dimensionAmount,
              currency: currency,
              type: DimensionEnums.DimensionCategory.Receipt,
              fileViewer: fileViewer,
            },
          },
        },
      },
    ];
  }, [receipt, dimensionAmount, currency, receiptCategories, paymentMethods, paymentAddActions, fileViewer]);

  return (
    <>
      <GenericForm formRef={ref} {...props} fields={formFields} inline />
      <CostForwardingDetailsSection isReadOnlyForm={props.isReadOnly} isGenericForm />
    </>
  );
};

export default forwardRef(ReceiptDetailsForm);
